import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import { imageAPI, imageUtils } from '../../services/imageApi';
import { isManagedMediaUrl } from '../../utils/mediaImageUtils';
import {
  ArrowLeft,
  Building2,
  Plus,
  Edit,
  Trash2,
  Eye,
  Upload,
  X,
  Save,
  MapPin,
  Star,
  Users,
  Camera,
  Image as ImageIcon
} from 'lucide-react';
import {
  ADMIN_ICON_STROKE,
  AdminPageHeader,
  AdminStatGrid,
  AdminSectionTitle,
  AdminTableIconButton,
  AdminTableIconButtonDanger,
  AdminSearchToolbar,
  AdminPaginationBar,
  paginateArray,
  DEFAULT_ADMIN_PAGE_SIZE
} from '../../components/admin/AdminShared';
import FoundersVerifiedBadge from '../../components/FoundersVerifiedBadge';
import DraggableStayImagesGrid from '../../components/DraggableStayImagesGrid';
import PawStoryVideoUpload from '../../components/PawStoryVideoUpload';
import ListingAttributesEditor from '../../components/ListingAttributesEditor';
import {
  createEmptyListingAttributes,
  mergeListingAttributes
} from '../../config/listingAttributesModel';

const RATING_FORM_KEYS = ['staff', 'location', 'roomComfort', 'cleanliness'];
const RATING_FORM_LABELS = {
  staff: 'Staff',
  location: 'Location',
  roomComfort: 'Room comfort',
  cleanliness: 'Cleanliness'
};

function emptyRatingsForm() {
  return {
    staff: { score: '', description: '' },
    location: { score: '', description: '' },
    roomComfort: { score: '', description: '' },
    cleanliness: { score: '', description: '' }
  };
}

function stayRatingsToForm(ratings) {
  const out = emptyRatingsForm();
  if (!ratings || typeof ratings !== 'object') return out;
  for (const k of RATING_FORM_KEYS) {
    const v = ratings[k];
    if (v && typeof v === 'object') {
      out[k] = {
        score: v.score !== undefined && v.score !== null ? String(v.score) : '',
        description: v.description != null ? String(v.description) : ''
      };
    }
  }
  return out;
}

const AdminStays = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action');

  const closeStayForm = () => {
    setShowForm(false);
    setEditingStay(null);
    if (searchParams.get('action')) {
      navigate('/admin/stays', { replace: true });
    }
  };

  const [stays, setStays] = useState([]);
  const [showForm, setShowForm] = useState(action === 'create' || action === 'edit');
  const [editingStay, setEditingStay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingFounderReviewSlide, setUploadingFounderReviewSlide] = useState(null);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);

  const [formData, setFormData] = useState({
    stayId: '',
    title: '',
    address: '',
    city: '',
    state: '',
    latitude: '',
    longitude: '',
    rating: 4.5,
    ratingTag: 'Very Good',
    founderVerified: false,
    inSpotlight: false,
    description: '',
    images: [],
    features: [],
    houseRules: [],
    nearestLandmarks: [],
    amenities: [],
    founderReviews: [],
    listingAttributes: createEmptyListingAttributes(),
    pawLoved: '',
    pawStoryVideoUrl: '',
    cancellationPolicy: '',
    ratings: emptyRatingsForm()
  });

  const [newFeature, setNewFeature] = useState({ title: '', image: '' });
  const [newRule, setNewRule] = useState({ ruleName: '', ruleValue: '' });
  const [newLandmark, setNewLandmark] = useState({ location: '', distance: '' });
  const [featureImageFile, setFeatureImageFile] = useState(null);
  const [uploadingFeatureImage, setUploadingFeatureImage] = useState(false);
  const [featureImagePreview, setFeatureImagePreview] = useState(null);
  const [useImageUpload, setUseImageUpload] = useState(false);
  const [newAmenity, setNewAmenity] = useState('');
  const [listSearch, setListSearch] = useState('');
  const [listPage, setListPage] = useState(1);

  // Fetch stays from API
  useEffect(() => {
    const fetchStays = async () => {
      try {
        setLoading(true);
        const response = await adminApi.getStays();
        setStays(response.stays || []);
      } catch (error) {
        console.error('Error fetching stays:', error);
        // Fallback to empty array on error
        setStays([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStays();
  }, []);

  useEffect(() => {
    const act = searchParams.get('action');
    if (act === 'create') {
      setShowForm(true);
      setEditingStay(null);
    }
  }, [searchParams]);

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    let previewUrls = [];
    try {
      setUploadingImages(true);
      previewUrls = files
        .filter((f) => f.type.startsWith('image/'))
        .map((file) => imageUtils.createPreviewUrl(file));
      if (previewUrls.length) setImagePreviewUrls((prev) => [...prev, ...previewUrls]);

      const { urls, errors } = await imageAPI.uploadGalleryMedia(files, 'stays');
      if (errors.length) alert(`Upload notes:\n${errors.join('\n')}`);
      if (urls.length) {
        setFormData((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
      } else if (errors.length) {
        throw new Error('No files uploaded');
      }

      previewUrls.forEach((url) => imageUtils.revokePreviewUrl(url));
      setImagePreviewUrls([]);
    } catch (error) {
      console.error('Gallery upload error:', error);
      const details =
        error.response?.data?.details || error.response?.data?.error || error.message;
      alert(details ? `Failed to upload gallery media: ${details}` : 'Failed to upload gallery media.');
      previewUrls.forEach((url) => imageUtils.revokePreviewUrl(url));
      setImagePreviewUrls([]);
    } finally {
      setUploadingImages(false);
      event.target.value = '';
    }
  };

  const handleFounderReviewImageUpload = async (slideIndex, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!imageUtils.isValidImageType(file)) {
      alert('Please select a valid image file (JPEG, PNG, WebP, GIF).');
      return;
    }
    if (!imageUtils.isValidFileSize(file)) {
      alert('File is too large. Maximum size is 10MB.');
      return;
    }
    try {
      setUploadingFounderReviewSlide(slideIndex);
      const response = await imageAPI.uploadImages([file], 'stays');
      if (response.uploaded && response.uploaded.length > 0) {
        const url = response.uploaded[0].url;
        setFormData((prev) => {
          const next = [...(prev.founderReviews || [])];
          next[slideIndex] = { ...next[slideIndex], image: url };
          return { ...prev, founderReviews: next };
        });
      } else {
        throw new Error('No URL returned');
      }
    } catch (error) {
      console.error('Founder review image upload:', error);
      const details =
        error.response?.data?.details || error.response?.data?.error || error.message;
      alert(details ? `Upload failed: ${details}` : 'Failed to upload image.');
    } finally {
      setUploadingFounderReviewSlide(null);
    }
  };

  const clearFounderReviewImage = (slideIndex) => {
    setFormData((prev) => {
      const next = [...(prev.founderReviews || [])];
      next[slideIndex] = { ...next[slideIndex], image: '' };
      return { ...prev, founderReviews: next };
    });
  };

  const reorderStayImages = (nextUrls) => {
    setFormData((prev) => ({ ...prev, images: nextUrls }));
  };

  const removeImage = async (index) => {
    const imageUrl = formData.images[index];
    
    if (imageUrl && isManagedMediaUrl(imageUrl)) {
      try {
        await imageAPI.deleteImage(imageUrl);
      } catch (error) {
        console.error('Failed to delete image from storage:', error);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addFeature = () => {
    if (newFeature.title && newFeature.image) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, { ...newFeature }]
      }));
      setNewFeature({ title: '', image: '' });
      setFeatureImageFile(null);
      setFeatureImagePreview(null);
      setUseImageUpload(false);
    }
  };

  const handleFeatureImageUpload = async (file) => {
    if (!file) return;

    // Validate file
    if (!imageUtils.isValidImageType(file)) {
      alert('Please select a valid image file (JPEG, PNG, WebP, GIF)');
      return;
    }

    if (!imageUtils.isValidFileSize(file)) {
      alert('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingFeatureImage(true);
      
      // Create preview
      const previewUrl = imageUtils.createPreviewUrl(file);
      setFeatureImagePreview(previewUrl);

      // Upload to S3
      const response = await imageAPI.uploadImages([file], 'features');
      
      if (response.uploaded && response.uploaded.length > 0) {
        const uploadedImage = response.uploaded[0];
        setNewFeature(prev => ({ ...prev, image: uploadedImage.url }));
        
        // Clean up preview
        imageUtils.revokePreviewUrl(previewUrl);
        setFeatureImagePreview(null);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Feature image upload error:', error);
      const details =
        error.response?.data?.details || error.response?.data?.error || error.message;
      alert(details ? `Failed to upload image: ${details}` : 'Failed to upload image. Please try again.');
      
      // Clean up preview on error
      if (featureImagePreview) {
        imageUtils.revokePreviewUrl(featureImagePreview);
        setFeatureImagePreview(null);
      }
    } finally {
      setUploadingFeatureImage(false);
    }
  };

  const handleFeatureImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFeatureImageFile(file);
      handleFeatureImageUpload(file);
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addRule = () => {
    if (newRule.ruleName && newRule.ruleValue) {
      setFormData(prev => ({
        ...prev,
        houseRules: [...prev.houseRules, { ...newRule }]
      }));
      setNewRule({ ruleName: '', ruleValue: '' });
    }
  };

  const removeRule = (index) => {
    setFormData(prev => ({
      ...prev,
      houseRules: prev.houseRules.filter((_, i) => i !== index)
    }));
  };

  const addLandmark = () => {
    if (newLandmark.location && newLandmark.distance) {
      setFormData(prev => ({
        ...prev,
        nearestLandmarks: [...prev.nearestLandmarks, { ...newLandmark }]
      }));
      setNewLandmark({ location: '', distance: '' });
    }
  };

  const removeLandmark = (index) => {
    setFormData(prev => ({
      ...prev,
      nearestLandmarks: prev.nearestLandmarks.filter((_, i) => i !== index)
    }));
  };

  const addAmenity = () => {
    if (newAmenity.trim()) {
      setFormData(prev => ({
        ...prev,
        amenities: [...prev.amenities, newAmenity.trim()]
      }));
      setNewAmenity('');
    }
  };

  const removeAmenity = (index) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Prepare data - convert empty latitude/longitude to null
      const submitData = {
        ...formData,
        latitude: formData.latitude === '' ? null : (formData.latitude ? parseFloat(formData.latitude) : null),
        longitude: formData.longitude === '' ? null : (formData.longitude ? parseFloat(formData.longitude) : null),
        founderReviews: (formData.founderReviews || [])
          .filter(
            (r) =>
              (r.quote && String(r.quote).trim()) || (r.image && String(r.image).trim())
          )
          .map((r) => ({
            quote: (r.quote && String(r.quote).trim()) || '',
            image: (r.image && String(r.image).trim()) || '',
            bubbleText: '',
            borderColor: (r.borderColor && String(r.borderColor).trim()) || ''
          }))
      };

      const builtRatings = {};
      for (const key of RATING_FORM_KEYS) {
        const row = formData.ratings?.[key];
        const desc = (row?.description && String(row.description).trim()) || '';
        const scoreNum =
          row?.score === '' || row?.score === undefined ? NaN : Number(row.score);
        if (desc && Number.isFinite(scoreNum)) {
          builtRatings[key] = { score: scoreNum, description: desc };
        }
      }
      const pawRaw =
        formData.pawLoved === '' || formData.pawLoved === undefined
          ? NaN
          : Number(formData.pawLoved);
      const pawValid = Number.isFinite(pawRaw) && pawRaw >= 1 && pawRaw <= 100;
      submitData.ratings = builtRatings;
      submitData.pawLoved = pawValid ? Math.round(pawRaw) : null;
      if (Object.keys(builtRatings).length === 0) {
        submitData.ratings = {};
        submitData.pawLoved = null;
      }
      
      let response;
      if (editingStay) {
        // Update existing stay
        response = await adminApi.updateStay(formData.stayId, submitData);
      } else {
        // Create new stay
        response = await adminApi.createStay(submitData);
      }
      
      console.log('Stay saved successfully:', response);
      
      // Reset form and close
      setFormData({
        stayId: '',
        title: '',
        address: '',
        city: '',
        state: '',
        latitude: '',
        longitude: '',
        rating: 4.5,
        ratingTag: 'Very Good',
        founderVerified: false,
        inSpotlight: false,
        description: '',
        images: [],
        features: [],
        houseRules: [],
        nearestLandmarks: [],
        amenities: [],
        founderReviews: [],
        listingAttributes: createEmptyListingAttributes(),
        pawLoved: '',
        pawStoryVideoUrl: '',
        cancellationPolicy: '',
        ratings: emptyRatingsForm()
      });
      setEditingStay(null);
      setShowForm(false);

      // Refresh the stays list
      const updatedStays = await adminApi.getStays();
      setStays(updatedStays.stays || []);

      if (searchParams.get('action')) {
        navigate('/admin/stays', { replace: true });
      }
      
    } catch (error) {
      console.error('Error saving stay:', error);
      alert('Error saving stay. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (stay) => {
    try {
      // Fetch full stay data including rooms
      const fullStayData = await adminApi.getStay(stay.stayId);
      setEditingStay(fullStayData);
      setFormData({ 
        ...fullStayData,
        latitude: fullStayData.latitude != null ? fullStayData.latitude.toString() : '',
        longitude: fullStayData.longitude != null ? fullStayData.longitude.toString() : '',
        founderReviews: Array.isArray(fullStayData.founderReviews) ? fullStayData.founderReviews : [],
        listingAttributes: mergeListingAttributes(fullStayData.listingAttributes),
        pawLoved:
          fullStayData.pawLoved != null && fullStayData.pawLoved !== ''
            ? String(fullStayData.pawLoved)
            : '',
        ratings: stayRatingsToForm(fullStayData.ratings)
      });
      setShowForm(true);
    } catch (error) {
      console.error('Error fetching stay details:', error);
      // Fallback to basic stay data
      setEditingStay(stay);
      setFormData({ 
        ...stay,
        latitude: stay.latitude != null ? stay.latitude.toString() : '',
        longitude: stay.longitude != null ? stay.longitude.toString() : '',
        founderReviews: Array.isArray(stay.founderReviews) ? stay.founderReviews : [],
        listingAttributes: mergeListingAttributes(stay.listingAttributes),
        pawLoved: stay.pawLoved != null && stay.pawLoved !== '' ? String(stay.pawLoved) : '',
        ratings: stayRatingsToForm(stay.ratings)
      });
      setShowForm(true);
    }
  };

  const handleDelete = async (stayId) => {
    if (window.confirm('Are you sure you want to delete this stay? This action cannot be undone.')) {
      try {
        setLoading(true);
        await adminApi.deleteStay(stayId);
        
        // Refresh the stays list
        const updatedStays = await adminApi.getStays();
        setStays(updatedStays.stays || []);
        
        alert('Stay deleted successfully!');
      } catch (error) {
        console.error('Error deleting stay:', error);
        alert('Error deleting stay. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const filteredStays = useMemo(() => {
    const q = listSearch.trim().toLowerCase();
    if (!q) return stays;
    return stays.filter((stay) => {
      const hay = [stay.title, stay.stayId, stay.city, stay.state, stay.address]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [stays, listSearch]);

  useEffect(() => {
    setListPage(1);
  }, [listSearch]);

  const { pageItems: pagedStays, total: filteredStayTotal, page: safeStayPage } = useMemo(
    () => paginateArray(filteredStays, listPage, DEFAULT_ADMIN_PAGE_SIZE),
    [filteredStays, listPage]
  );

  if (showForm) {
    return (
      <div>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={closeStayForm}
              className="p-2.5 rounded-[12px] bg-[#FAF7F2] text-[#FF5E4C] hover:opacity-90 shrink-0 transition-opacity inline-flex items-center justify-center relative z-10"
              aria-label="Back to stays list"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={ADMIN_ICON_STROKE} aria-hidden />
            </button>
            <div>
              <h1 className="text-[20px] md:text-[22px] font-medium text-black tracking-[-0.02em]">
                {editingStay ? 'Edit stay' : 'Add stay'}
              </h1>
              <p className="text-[14px] text-[#78736A] mt-1">Pet-friendly accommodation details</p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeStayForm}
            className="px-5 py-2.5 border border-[#E5E3DF] bg-white text-black rounded-full hover:bg-[#FAF7F2] text-[14px] font-medium transition-colors inline-flex items-center gap-2"
          >
            Cancel
          </button>
        </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">Basic information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Stay ID *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.stayId}
                    onChange={(e) => setFormData(prev => ({ ...prev, stayId: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="e.g., s_001"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="e.g., Pawsome Paradise Resort & Spa"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Address *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="Complete address with landmarks"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="e.g., Mumbai"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.state}
                    onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="e.g., Maharashtra"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Latitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="e.g., 19.0760"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Longitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="e.g., 72.8777"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Rating (0-5)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={formData.rating}
                    onChange={(e) => setFormData(prev => ({ ...prev, rating: parseFloat(e.target.value) }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    Rating Tag
                  </label>
                  <input
                    type="text"
                    value={formData.ratingTag}
                    onChange={(e) => setFormData(prev => ({ ...prev, ratingTag: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="e.g., Paw-some Experience"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">
                    About the property
                  </label>
                  <p className="text-[12px] text-[#78736A] mb-2">
                    Shown in the About the property section on the stay detail page.
                  </p>
                  <textarea
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="Describe the stay, its unique features, and what makes it special for pets and their owners..."
                  />
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">Gallery photos & videos</h2>
              
              {/* Upload Section */}
              <div className="border-2 border-dashed border-[#E5E3DF] rounded-[12px] p-6 mb-6 bg-[#FAF7F2]/40">
                <div className="text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Upload gallery photos & videos</p>
                  <p className="text-sm text-gray-500 mb-4">Photos (max 10MB) or videos MP4/MOV/WebM (raw up to 500MB; server compresses videos)</p>
                  
                  {uploadingImages && (
                    <div className="mb-4">
                      <div className="inline-flex items-center px-4 py-2 bg-[#FF5E4C]/10 text-[#FF5E4C] rounded-[12px]">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#FF5E4C] border-t-transparent mr-2"></div>
                        Uploading images...
                      </div>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    multiple
                    accept={imageUtils.galleryVideoAccept}
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                    disabled={uploadingImages}
                  />
                  <label
                    htmlFor="image-upload"
                    className="inline-flex items-center px-4 py-2 bg-[#FF5E4C] text-white rounded-full hover:opacity-90 cursor-pointer transition-colors"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Choose Images
                  </label>
                </div>
              </div>

              {/* Image Preview */}
              {(formData.images.length > 0 || imagePreviewUrls.length > 0) && (
                <div className="space-y-4">
                  <DraggableStayImagesGrid
                    images={formData.images}
                    onReorder={reorderStayImages}
                    onRemove={removeImage}
                    hintClassName="text-sm text-[#78736A] mb-3"
                  />
                  {imagePreviewUrls.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {imagePreviewUrls.map((previewUrl, index) => (
                        <div key={`preview-${index}`} className="relative group">
                          <img
                            src={previewUrl}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg opacity-50"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FF5E4C] border-t-transparent"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Features */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">Features</h2>
              
              {/* Add Feature */}
              <div className="space-y-4 mb-4">
                {/* Feature Title */}
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">Feature Title</label>
                  <input
                    type="text"
                    value={newFeature.title}
                    onChange={(e) => setNewFeature(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="Enter feature title"
                  />
                </div>

                {/* Image Input Options */}
                <div>
                  <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">Feature Image</label>
                  
                  {/* Toggle between URL and Upload */}
                  <div className="flex space-x-2 mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        setUseImageUpload(false);
                        setNewFeature(prev => ({ ...prev, image: '' }));
                        setFeatureImageFile(null);
                        setFeatureImagePreview(null);
                      }}
                      className={`px-3 py-1 text-sm rounded-md ${
                        !useImageUpload ? 'bg-[#FF5E4C]/15 text-[#FF5E4C]' : 'bg-[#FAF7F2] text-[#78736A] border border-[#E5E3DF]'
                      }`}
                    >
                      Enter URL
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setUseImageUpload(true);
                        setNewFeature(prev => ({ ...prev, image: '' }));
                        setFeatureImageFile(null);
                        setFeatureImagePreview(null);
                      }}
                      className={`px-3 py-1 text-sm rounded-md ${
                        useImageUpload ? 'bg-[#FF5E4C]/15 text-[#FF5E4C]' : 'bg-[#FAF7F2] text-[#78736A] border border-[#E5E3DF]'
                      }`}
                    >
                      Upload Image
                    </button>
                  </div>

                  {/* URL Input */}
                  {!useImageUpload && (
                    <input
                      type="url"
                      value={newFeature.image}
                      onChange={(e) => setNewFeature(prev => ({ ...prev, image: e.target.value }))}
                      className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                      placeholder="https://example.com/image.jpg"
                    />
                  )}

                  {/* File Upload */}
                  {useImageUpload && (
                    <div className="space-y-3">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFeatureImageChange}
                        className="hidden"
                        id="feature-image-upload"
                        disabled={uploadingFeatureImage}
                      />
                      <label
                        htmlFor="feature-image-upload"
                        className="flex items-center justify-center w-full px-4 py-2 border-2 border-dashed border-[#E5E3DF] rounded-[12px] cursor-pointer hover:border-[#FF5E4C]/50 hover:bg-[#FAF7F2] transition-colors"
                      >
                        <div className="text-center">
                          <ImageIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">
                            {uploadingFeatureImage ? 'Uploading...' : 'Click to upload image'}
                          </p>
                          <p className="text-xs text-gray-500">Max 10MB, JPEG, PNG, WebP, GIF</p>
                        </div>
                      </label>

                      {/* Upload Progress */}
                      {uploadingFeatureImage && (
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#FF5E4C] border-t-transparent"></div>
                          <span className="text-sm text-[#FF5E4C]">Uploading image...</span>
                        </div>
                      )}

                      {/* Image Preview */}
                      {featureImagePreview && (
                        <div className="relative">
                          <img
                            src={featureImagePreview}
                            alt="Feature preview"
                            className="w-20 h-20 object-cover rounded-lg border"
                          />
                        </div>
                      )}

                      {/* Uploaded Image Display */}
                      {newFeature.image && !uploadingFeatureImage && (
                        <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                          <img
                            src={newFeature.image}
                            alt="Uploaded feature"
                            className="w-12 h-12 object-cover rounded"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-green-800">Image uploaded successfully</p>
                            <p className="text-xs text-green-600 truncate">{newFeature.image}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setNewFeature(prev => ({ ...prev, image: '' }));
                              setFeatureImageFile(null);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Add Button */}
                <button
                  type="button"
                  onClick={addFeature}
                  disabled={!newFeature.title || !newFeature.image || uploadingFeatureImage}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingFeatureImage ? 'Uploading...' : 'Add Feature'}
                </button>
              </div>

              {/* Features List */}
              <div className="space-y-3">
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-[12px] border border-[#E5E3DF]/60">
                    <div className="flex items-center space-x-3">
                      {feature.image ? (
                        <img
                          src={feature.image}
                          alt={feature.title}
                          className="w-10 h-10 object-cover rounded-lg border"
                        />
                      ) : (
                        <ImageIcon className="w-5 h-5 text-gray-400" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{feature.title}</p>
                        <p className="text-sm text-gray-500 truncate max-w-xs">{feature.image}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Paw ratings (public property page) */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6 space-y-6">
              <div>
                <h2 className="text-[16px] font-medium text-black mb-1">Paw ratings</h2>
                <p className="text-[13px] text-[#78736A]">
                  Optional. The Paw Meter and category scores on the property page only appear after you save at least
                  one category with a score and description. Set Paw loved (1–100) to show the meter bar.
                </p>
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#5c5c5c] mb-2">Paw loved (%)</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  step={1}
                  value={formData.pawLoved}
                  onChange={(e) => setFormData((prev) => ({ ...prev, pawLoved: e.target.value }))}
                  className="w-full max-w-xs px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  placeholder="e.g. 85"
                />
              </div>
              <div className="space-y-5">
                {RATING_FORM_KEYS.map((key) => (
                  <div
                    key={key}
                    className="rounded-[12px] border border-[#E5E3DF]/80 bg-[#FAF7F2]/50 p-4"
                  >
                    <h3 className="text-[14px] font-medium text-black mb-3">
                      {RATING_FORM_LABELS[key]}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-[12px] text-[#78736A] mb-1">Score (0–10)</label>
                        <input
                          type="number"
                          min={0}
                          max={10}
                          step={0.1}
                          value={formData.ratings[key].score}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              ratings: {
                                ...prev.ratings,
                                [key]: { ...prev.ratings[key], score: e.target.value }
                              }
                            }))
                          }
                          className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                          placeholder="8.5"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-[12px] text-[#78736A] mb-1">Description</label>
                        <textarea
                          rows={2}
                          value={formData.ratings[key].description}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              ratings: {
                                ...prev.ratings,
                                [key]: { ...prev.ratings[key], description: e.target.value }
                              }
                            }))
                          }
                          className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C] resize-y min-h-[72px]"
                          placeholder="Short blurb for guests"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Paw Stories reel (homepage / stays strip) */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-1">Paw Stories reel</h2>
              <p className="text-[13px] text-[#78736A] mb-4">
                Optional. Upload an MP4 / MOV / WebM reel (up to 500MB raw; compressed to about 10MB max) to S3, or paste a full URL or site path
                like <code className="text-[12px]">/reels/your-file.mp4</code>.
              </p>
              <PawStoryVideoUpload
                variant="admin"
                videoUrl={formData.pawStoryVideoUrl || ''}
                onVideoUrlChange={(url) => setFormData((prev) => ({ ...prev, pawStoryVideoUrl: url }))}
              />
            </div>

            {/* Cancellation Policy */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-1">Cancellation Policy</h2>
              <p className="text-sm text-gray-500 mb-4">
                Shown to guests on the stay booking review page before payment.
              </p>
              <textarea
                value={formData.cancellationPolicy || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, cancellationPolicy: e.target.value }))}
                rows={5}
                className="w-full px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C] resize-y"
                placeholder="e.g. Free cancellation until 7 days before check-in. Cancellations within 7 days are non-refundable."
              />
            </div>

            {/* House Rules */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">House Rules</h2>
              
              {/* Add Rule */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <input
                  type="text"
                  value={newRule.ruleName}
                  onChange={(e) => setNewRule(prev => ({ ...prev, ruleName: e.target.value }))}
                  className="px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  placeholder="Rule name"
                />
                <input
                  type="text"
                  value={newRule.ruleValue}
                  onChange={(e) => setNewRule(prev => ({ ...prev, ruleValue: e.target.value }))}
                  className="px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  placeholder="Rule value"
                />
                <button
                  type="button"
                  onClick={addRule}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Add Rule
                </button>
              </div>

              {/* Rules List */}
              <div className="space-y-3">
                {formData.houseRules.map((rule, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-[12px] border border-[#E5E3DF]/60">
                    <div>
                      <p className="font-medium text-gray-900">{rule.ruleName}</p>
                      <p className="text-sm text-gray-500">{rule.ruleValue}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRule(index)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Nearest Landmarks */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">Nearest Landmarks</h2>
              
              {/* Add Landmark */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <input
                  type="text"
                  value={newLandmark.location}
                  onChange={(e) => setNewLandmark(prev => ({ ...prev, location: e.target.value }))}
                  className="px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  placeholder="Landmark name"
                />
                <input
                  type="text"
                  value={newLandmark.distance}
                  onChange={(e) => setNewLandmark(prev => ({ ...prev, distance: e.target.value }))}
                  className="px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                  placeholder="Distance (e.g., 2.5 km)"
                />
                <button
                  type="button"
                  onClick={addLandmark}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Add Landmark
                </button>
              </div>

              {/* Landmarks List */}
              <div className="space-y-3">
                {formData.nearestLandmarks.map((landmark, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-[12px] border border-[#E5E3DF]/60">
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">{landmark.location}</p>
                        <p className="text-sm text-gray-500">{landmark.distance}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeLandmark(index)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Amenities: structured search filters + optional tags for stay pages */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6 space-y-8">
              <div>
                <h2 className="text-[16px] font-medium text-black mb-1">Amenities</h2>
                <p className="text-[13px] text-[#78736A]">
                  Set the options below for search filters. Leave unset where not applicable. Add extra tags at the bottom for anything else you want to highlight on the stay page.
                </p>
              </div>
              <ListingAttributesEditor
                value={formData.listingAttributes}
                onChange={(next) =>
                  setFormData((prev) => ({ ...prev, listingAttributes: next }))
                }
              />
              <div className="border-t border-[#E5E3DF] pt-6">
                <h3 className="text-[14px] font-medium text-black mb-1">Additional amenities</h3>
                <p className="text-[12px] text-[#78736A] mb-4">
                  Short labels shown on the listing (not used for structured search filters).
                </p>
                <div className="flex gap-4 mb-4">
                  <input
                    type="text"
                    value={newAmenity}
                    onChange={(e) => setNewAmenity(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addAmenity();
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-[#E5E3DF] rounded-[12px] focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
                    placeholder="e.g. Pet sitter on request"
                  />
                  <button
                    type="button"
                    onClick={addAmenity}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shrink-0"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center space-x-2 bg-[#FAF7F2] text-black border border-[#E5E3DF] px-3 py-1 rounded-full">
                      <span className="text-sm">{amenity}</span>
                      <button
                        type="button"
                        onClick={() => removeAmenity(index)}
                        className="text-[#FF5E4C] hover:opacity-80"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Founder reviews carousel (property page) */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-2">Founder reviews</h2>
              <p className="text-[13px] text-[#5c5c5c] mb-4">
                Pinboard on the property page: each slide is a sticky note with your <strong>quote</strong> and an optional <strong>photo</strong> that overlaps the bottom edge. Add a photo URL or upload for the “pasted polaroid” look.
              </p>
              <div className="space-y-4">
                {(formData.founderReviews || []).map((item, index) => (
                  <div
                    key={index}
                    className="border border-[#E5E3DF] rounded-[12px] p-4 space-y-3 bg-[#FAF7F2]"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-[13px] font-medium text-black">Slide {index + 1}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            founderReviews: prev.founderReviews.filter((_, i) => i !== index)
                          }))
                        }
                        className="text-[#FF5E4C] text-sm hover:opacity-80"
                      >
                        Remove
                      </button>
                    </div>
                    <label className="block text-[13px] text-[#5c5c5c]">
                      Quote / caption
                      <textarea
                        value={item.quote || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFormData((prev) => {
                            const next = [...(prev.founderReviews || [])];
                            next[index] = { ...next[index], quote: v };
                            return { ...prev, founderReviews: next };
                          });
                        }}
                        rows={3}
                        className="mt-1 w-full border border-[#E5E3DF] rounded-lg px-3 py-2 text-sm text-black"
                        placeholder="Okay, so I sniffed the water first..."
                      />
                    </label>
                    <div className="text-[13px] text-[#5c5c5c]">
                      <span className="block mb-1">Polaroid photo (optional)</span>
                      <div className="mt-1 flex flex-wrap items-start gap-3">
                        <div>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            className="hidden"
                            id={`founder-review-upload-${index}`}
                            disabled={uploadingFounderReviewSlide === index}
                            onChange={(e) => handleFounderReviewImageUpload(index, e)}
                          />
                          <label
                            htmlFor={`founder-review-upload-${index}`}
                            className={`inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#E5E3DF] bg-white px-4 py-2 text-sm text-black transition hover:bg-[#FAF7F2] ${
                              uploadingFounderReviewSlide === index ? 'pointer-events-none opacity-60' : ''
                            }`}
                          >
                            <Upload className="h-4 w-4 shrink-0 text-[#5c5c5c]" strokeWidth={ADMIN_ICON_STROKE} />
                            {uploadingFounderReviewSlide === index ? 'Uploading…' : 'Upload photo'}
                          </label>
                        </div>
                        {item.image ? (
                          <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-[#E5E3DF] bg-white">
                            <img src={item.image} alt="" className="h-full w-full object-cover" />
                            <button
                              type="button"
                              onClick={() => clearFounderReviewImage(index)}
                              className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
                              title="Remove photo"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <label className="mt-2 block">
                        <span className="text-[12px] text-[#888]">Or paste image URL</span>
                        <input
                          type="url"
                          value={item.image || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setFormData((prev) => {
                              const next = [...(prev.founderReviews || [])];
                              next[index] = { ...next[index], image: v };
                              return { ...prev, founderReviews: next };
                            });
                          }}
                          className="mt-1 w-full border border-[#E5E3DF] rounded-lg px-3 py-2 text-sm"
                          placeholder="https://..."
                        />
                      </label>
                    </div>
                    <label className="block text-[13px] text-[#5c5c5c]">
                      Polaroid border color (optional, e.g. #FF5E4C)
                      <input
                        type="text"
                        value={item.borderColor || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFormData((prev) => {
                            const next = [...(prev.founderReviews || [])];
                            next[index] = { ...next[index], borderColor: v };
                            return { ...prev, founderReviews: next };
                          });
                        }}
                        className="mt-1 w-full border border-[#E5E3DF] rounded-lg px-3 py-2 text-sm font-mono"
                        placeholder="#FF5E4C"
                      />
                    </label>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      founderReviews: [
                        ...(prev.founderReviews || []),
                        { quote: '', image: '', borderColor: '' }
                      ]
                    }))
                  }
                  className="px-4 py-2 border border-[#E5E3DF] rounded-full text-[14px] text-black hover:bg-[#FAF7F2]"
                >
                  + Add founder review slide
                </button>
              </div>
            </div>

            {/* Settings */}
            <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF] p-5 md:p-6">
              <h2 className="text-[16px] font-medium text-black mb-6">Settings</h2>
              <div className="space-y-4">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.founderVerified}
                    onChange={(e) => setFormData(prev => ({ ...prev, founderVerified: e.target.checked }))}
                    className="w-4 h-4 text-[#FF5E4C] border-[#E5E3DF] rounded focus:ring-[#FF5E4C]/30"
                  />
                  <span className="text-[#5c5c5c]">Founder Verified</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.inSpotlight}
                    onChange={(e) => setFormData(prev => ({ ...prev, inSpotlight: e.target.checked }))}
                    className="w-4 h-4 text-[#FF5E4C] border-[#E5E3DF] rounded focus:ring-[#FF5E4C]/30"
                  />
                  <span className="text-[#5c5c5c]">Featured in Spotlight</span>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={closeStayForm}
                className="px-6 py-3 text-[#5c5c5c] border border-[#E5E3DF] bg-white hover:bg-[#FAF7F2] rounded-full transition-colors text-[14px] font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-[#FF5E4C] text-white rounded-full hover:opacity-90 disabled:opacity-50 transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                <span>{loading ? 'Saving...' : 'Save Stay'}</span>
              </button>
            </div>
          </form>
      </div>
    );
  }

  const stayStatItems = [
    { label: 'Total stays', value: stays.length, icon: Building2 },
    {
      label: 'Spotlight',
      value: stays.filter((s) => s.inSpotlight).length,
      icon: Star
    },
    {
      label: 'Verified',
      value: stays.filter((s) => s.founderVerified).length,
      icon: Users
    },
    {
      label: 'Avg rating',
      value:
        stays.length > 0
          ? (stays.reduce((sum, s) => sum + s.rating, 0) / stays.length).toFixed(1)
          : '0.0',
      icon: Star
    }
  ];

  return (
    <div>
      <AdminPageHeader
        title="Stays"
        subtitle="Add and manage pet-friendly accommodations"
      >
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="px-5 py-2.5 bg-[#FF5E4C] text-white rounded-full hover:opacity-90 transition-opacity inline-flex items-center gap-2 text-[14px] font-medium"
        >
          <Plus className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
          <span>Add stay</span>
        </button>
      </AdminPageHeader>

      <AdminSearchToolbar
        placeholder="Search by stay name, city, state, or stay ID…"
        value={listSearch}
        onChange={(e) => setListSearch(e.target.value)}
      />

      <div className="mt-4 space-y-6">
        <AdminStatGrid items={stayStatItems} loading={false} />

        {/* Stays List */}
        <div className="bg-white rounded-[16px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] border border-[#E5E3DF]">
          <div className="p-5 md:p-6 border-b border-[#E5E3DF]">
            <AdminSectionTitle>All stays</AdminSectionTitle>
          </div>
          {pagedStays.length === 0 ? (
            <div className="p-10 text-center text-[#78736A] text-[14px]">
              No stays match your search.
            </div>
          ) : (
          <div className="divide-y divide-[#E5E3DF]">
            {pagedStays.map((stay) => (
              <div key={stay.stayId} className="p-5 md:p-6 hover:bg-[#FAF7F2]/50 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-4 min-w-0">
                    <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden shrink-0">
                      {stay.images && stay.images.length > 0 ? (
                        <img
                          src={stay.images[0]}
                          alt={stay.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-[16px] font-medium text-black truncate">{stay.title}</h3>
                      </div>
                      <p className="text-gray-600 truncate">{stay.city}, {stay.state}</p>
                      <div className="flex items-center space-x-4 mt-1">
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                          <span className="text-sm text-gray-600">{stay.rating}</span>
                        </div>
                        {stay.founderVerified && (
                          <FoundersVerifiedBadge size="sm" />
                        )}
                        {stay.inSpotlight && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                            Spotlight
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <AdminTableIconButton onClick={() => handleEdit(stay)} title="Edit">
                      <Edit className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                    </AdminTableIconButton>
                    <AdminTableIconButton title="View">
                      <Eye className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                    </AdminTableIconButton>
                    <AdminTableIconButtonDanger
                      onClick={() => handleDelete(stay.stayId)}
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={ADMIN_ICON_STROKE} />
                    </AdminTableIconButtonDanger>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
          <AdminPaginationBar
            page={safeStayPage}
            pageSize={DEFAULT_ADMIN_PAGE_SIZE}
            total={filteredStayTotal}
            onPageChange={setListPage}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminStays;